import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import ProductShowcase from './components/ProductShowcase'; 
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import OrderForm from './components/OrderForm';
import ProductPage from './components/ProductPage';
import WhatsAppFloat from './components/WhatsAppFloat';
import { Analytics } from '@vercel/analytics/react';

function App() {
  const [preselectedProductId, setPreselectedProductId] = useState<string | undefined>(undefined);

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productParam = urlParams.get('product');
    if (productParam) {
      setPreselectedProductId(productParam);
      
      // Scroll to order form after a short delay
      setTimeout(() => {
        const orderSection = document.getElementById('order');
        if (orderSection) {
          const offsetTop = orderSection.offsetTop - 80;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
    
    return () => {
      document.body.classList.remove('font-arabic');
    };
  }, []);

  // Handle order click with optional product preselection
  const handleOrderClick = (productId?: string) => {
    setPreselectedProductId(productId);
    
    // Scroll to order form
    const orderSection = document.getElementById('order');
    if (orderSection) {
      const offsetTop = orderSection.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-white overflow-x-hidden">
        <Routes>
          <Route path="/" element={
            <>
              {/* Header */}
              <Header onOrderClick={() => handleOrderClick()} />
              
              {/* Main Content */}
              <main>
                {/* Order Form Section (moved to top, replacing Hero) */}
                <OrderForm preselectedProductId={preselectedProductId} />
                
                {/* Product Showcase */}
                <ProductShowcase onOrderClick={handleOrderClick} />
                
                {/* How It Works */}
                <HowItWorks />
                
                {/* Testimonials & Pricing */}
                <Pricing onOrderClick={() => handleOrderClick()} />
              </main>

              {/* Footer */}
              <footer className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
                    
                    {/* Company Info */}
                    <div className="md:col-span-2 lg:col-span-1">
                      <div className="flex items-center space-x-3 mb-6">
                        <img 
                          src="https://i.ibb.co/n85QCGDP/totalelogo.png" 
                          alt="Totale Sécurité" 
                          className="h-12 w-auto"
                        />
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-right">Totale Sécurité</h3>
                      <p className="text-red-100 leading-relaxed mb-6 text-right">
                        الرائد في تونس في مجال حماية السيارات من السرقة. 
                        أكثر من 1500 عميل راضٍ عن منتجاتنا وخدماتنا المميزة.
                      </p>
                      
                      {/* Social Media */}
                      <div className="flex justify-end space-x-4">
                        <a 
                          href="https://www.facebook.com/profile.php?id=61572320684540" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </a>
                        
                        <a 
                          href="https://www.tiktok.com/@bloocpeedal" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.7v4.04c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.4 3.94-1.55 2.14-4.21 3.41-6.8 3.3-1.46-.05-2.9-.5-4.13-1.29-1.09-.71-2.04-1.65-2.77-2.72-.31-.43-.59-.87-.84-1.34.65-.37 1.31-.74 1.95-1.13.57.88 1.28 1.67 2.12 2.3.84.63 1.84.99 2.9 1.04 1.06.05 2.14-.17 3.06-.72.93-.54 1.65-1.36 2.13-2.31.48-.95.71-2.02.68-3.09-.04-1.18-.01-2.35-.01-3.53 0-4.24 0-8.47 0-12.71z"/>
                          </svg>
                        </a>

                        <a 
                          href={`https://wa.me/21653231142?text=${encodeURIComponent('مرحبا! أريد معلومات عن منتجاتكم')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.687"/>
                          </svg>
                        </a>
                      </div>
                    </div>
                    
                    {/* Quick Links */}
                    <div className="text-right">
                      <h4 className="font-bold text-xl mb-6 text-white">روابط سريعة</h4>
                      <div className="space-y-3">
                        {[
                          { label: 'الرئيسية', href: '#order' },
                          { label: 'المنتجات', href: '#products' },
                          { label: 'طريقة العمل', href: '#how-it-works' },
                          { label: 'آراء العملاء', href: '#testimonials' },
                          { label: 'اطلب الآن', href: '#order' }
                        ].map((link, index) => (
                          <a 
                            key={index}
                            href={link.href}
                            className="block text-red-200 hover:text-white transition-colors duration-300 hover:translate-x-1"
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="text-right">
                      <h4 className="font-bold text-xl mb-6 text-white">معلومات التواصل</h4>
                      <div className="space-y-4">
                        <div>
                          <p className="text-red-200 text-sm mb-1">الهاتف</p>
                          <a 
                            href="tel:+21653231142"
                            className="font-semibold text-white hover:text-red-200 transition-colors duration-300 text-lg"
                          >
                            +216 53 231 142
                          </a>
                        </div>
                        <div>
                          <p className="text-red-200 text-sm mb-1">أوقات العمل</p>
                          <p className="text-white">9:00 - 22:00</p>
                          <p className="text-red-200 text-sm">كل أيام الأسبوع</p>
                        </div>
                        <div>
                          <p className="text-red-200 text-sm mb-1">المنطقة</p>
                          <p className="text-white">جميع أنحاء تونس</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Services */}
                    <div className="text-right">
                      <h4 className="font-bold text-xl mb-6 text-white">خدماتنا</h4>
                      <div className="space-y-3">
                        {[
                          'أجهزة مكافحة السرقة',
                          'توصيل مجاني',
                          'دعم فني متواصل',
                          'ضمان سنة كاملة',
                          'دفع عند الاستلام'
                        ].map((service, index) => (
                          <div key={index} className="text-red-200 hover:text-white transition-colors duration-300">
                            ✓ {service}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Service Highlights */}
                  <div className="border-t border-red-500 pt-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">🚚</span>
                        </div>
                        <h5 className="font-bold mb-2 text-white">توصيل مجاني</h5>
                        <p className="text-red-200 text-sm">لجميع أنحاء الجمهورية التونسية</p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">💳</span>
                        </div>
                        <h5 className="font-bold mb-2 text-white">دفع آمن</h5>
                        <p className="text-red-200 text-sm">دفع بالدينار التونسي عند الاستلام</p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">🛡️</span>
                        </div>
                        <h5 className="font-bold mb-2 text-white">ضمان موثوق</h5>
                        <p className="text-red-200 text-sm">ضمان لمدة 12 شهر على جميع المنتجات</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Trust Badges */}
                  <div className="mt-6 pt-6 border-t border-red-500/30">
                    <p className="text-red-200 text-center text-sm leading-relaxed">
                      نحن ملتزمون بتقديم أفضل منتجات الحماية للسيارات في تونس. 
                      منتجاتنا مجربة ومعتمدة من آلاف العملاء الراضين.
                    </p>
                  </div>
                </div>
              </footer>
              <WhatsAppFloat />
              <Analytics />
            </>
          } />
          <Route path="/product/:productId" element={<ProductPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;